import { FC, useEffect, useMemo } from 'react';
import { Vector3 } from 'three';
import { MetricKeyEnum, useLayerStore } from '../LayerControls/layerStore';
import { ThreeDPoint } from '../3dModels/ThreeDPoint';
import { Line3D } from '../3dModels/JointRenderer';
import { jointLocationDict } from '../../types/sagittalTypes';
import { FeaturesetValues } from '../../network';
import { ThreeDLabel } from '../3dModels/ThreeDLabel';
import { useHighlighter } from '../ui/useHighlighter';

interface HipToEarProps {
  featuresetValues: FeaturesetValues | undefined;
}

const layerKey = MetricKeyEnum.HipToEar;

export const HipToEar: FC<HipToEarProps> = ({ featuresetValues }) => {
  const { addLayerState, isLayerVisible } = useLayerStore();
  useHighlighter({ layerKey });
  const result = useMemo(() => {
    try {
      const angle = featuresetValues?.hip_to_ear_angle;
      const sagittalDisplayDict = featuresetValues?.sagittal_display_dict;
      const jointLocations = sagittalDisplayDict?.joint_locations;

      if (!jointLocations) {
        throw new Error('Joint locations are not defined for hip to ear');
      }
      if (!angle) {
        throw new Error('Angle is not defined for hip to ear');
      }

      const leftEar = jointLocations[jointLocationDict.left_ear];
      const rightEar = jointLocations[jointLocationDict.right_ear];
      const leftHip = jointLocations[jointLocationDict.left_hip];
      const rightHip = jointLocations[jointLocationDict.right_hip];

      const leftHipVector = new Vector3(...leftHip);
      const rightHipVector = new Vector3(...rightHip);
      const leftEarVector = new Vector3(...leftEar);
      const rightEarVector = new Vector3(...rightEar);

      const hipAvg = leftHipVector.add(rightHipVector).divideScalar(2);
      const earAvg = leftEarVector.add(rightEarVector).divideScalar(2);
      const height = earAvg.y * 1.2;
      const verticalStart = new Vector3(hipAvg.x, 0, hipAvg.z);
      const verticalEnd = new Vector3(hipAvg.x, height, hipAvg.z);

      return {
        hipAvg: hipAvg.toArray(),
        earAvg: earAvg.toArray(),
        verticalStart: verticalStart.toArray(),
        verticalEnd: verticalEnd.toArray(),
        angle: angle,
      };
    } catch (error) {
      console.error(error);
      return null;
    }
  }, [
    featuresetValues?.hip_to_ear_angle,
    featuresetValues?.sagittal_display_dict,
  ]);

  useEffect(() => {
    if (!result) {
      return;
    }
    addLayerState(layerKey, {
      key: layerKey,
      visible: false,
    });
  }, [addLayerState, result]);

  const showLayer = isLayerVisible(layerKey);

  if (!result) {
    return null;
  }

  return (
    <group name={layerKey} visible={showLayer}>
      <ThreeDPoint position={result.hipAvg} alwaysOnTop />
      <ThreeDPoint position={result.earAvg} alwaysOnTop />
      <Line3D start={result.hipAvg} end={result.earAvg} />
      <Line3D
        start={result.verticalStart}
        end={result.verticalEnd}
        color='blue'
      />
      <ThreeDLabel
        plane='sagittal'
        position={result.earAvg}
        label={`${result.angle.toFixed(2)}°`}
        visible={showLayer}
        divPosition={{
          bottom: 10,
          right: 10,
        }}
        layerKey={layerKey}
      />
    </group>
  );
};
